import type { UserData } from "@/api/auth";

import { type PiiLaneUser, piiLaneFor } from "@/utils/piiLane";

import { type FlagUserKind, refreshFeatureFlags, resetFeatureFlags } from "./featureFlags";
import {
  identifyForFlags,
  isInternalUser,
  resetIdentity,
  setCurrentUserInternal,
  shouldTrack,
} from "./mixpanel";
import { getSegment } from "./segment";

// Frontend identify: userId + email for HubSpot compatibility (doesn't auto-alias like Mixpanel)
// Backend remains source of truth for full user traits
export function identifyUser(userId: string, email: string): void {
  const internal = isInternalUser(email);
  setCurrentUserInternal(internal);

  // Identify in Mixpanel regardless of tracking suppression so the browser profile carries the
  // same distinct_id the server evaluates flags for. This only links a profile — noisy events
  // and session recording stay gated by shouldTrack() below. Flags themselves are resolved by
  // RAP's backend (RAP-668), so a blocked Mixpanel no longer affects them.
  identifyForFlags(userId);
  void refreshFeatureFlags();

  if (!shouldTrack()) return;

  // Segment: userId + email (HubSpot needs email to identify contacts)
  getSegment()?.identify(userId, { email });
}

/**
 * The firm a user belongs to, for flag targeting (mirrors the server's derivation): the identity anchor (root identity), or the
 * user's own org when it has none — an org with no root IS its own anchor. `rootIdentity` only
 * arrives with /auth/me, so the slim login payload resolves to the org until that lands.
 */
function flagFirmFor(user: Pick<UserData, "organization" | "rootIdentity">): {
  firmId: string;
  firmName: string;
} | null {
  if (user.rootIdentity) return { firmId: user.rootIdentity.id, firmName: user.rootIdentity.name };
  if (user.organization) return { firmId: user.organization.id, firmName: user.organization.name };
  return null;
}

/**
 * The kind of user, for flag targeting (`user_kind`), derived from the shared PII lane
 * (`piiLaneFor`) so it never drifts from the store's gates:
 * - `external` — no org (view-assigned);
 * - `funder` — a referring-portal firm member without view_pii;
 * - `referring` — a referring-portal firm member with view_pii, OR any non-portal REFERRING org
 *   (the org-type fallback) — so `referring` does NOT mean "portal firm"; target a portal firm by
 *   `firm_id`;
 * - otherwise the org's type in lower case (`handling`, `marketing`, `admin`, …). Handling-firm
 *   members without view_pii also read `handling`; they have no chat to switch off anyway.
 * Before /auth/me lands the slim login payload lacks `referringPortalEnabled`, so a funder reads
 * as its org type until then; `syncFeatureFlagFirm` re-resolves when the kind changes.
 */
export function flagUserKindFor(user: PiiLaneUser): FlagUserKind {
  const lane = piiLaneFor(user);
  if (lane === "external") return "external";
  if (lane === "funder") return "funder";
  if (user.referringPortalEnabled === true) return "referring";
  return user.organization?.orgType?.toLowerCase() ?? "unknown";
}

let currentFlagKey: null | string = null;

/**
 * Re-resolve flags when the user's firm or kind changes. The server derives the targeting context
 * itself on every request (RAP-668), so nothing is sent from here — but a change seen here (e.g. a
 * role change picked up by /auth/me, which moves `user_kind`) means the cached answer may be for
 * the old context. Called before `identifyUser` on every identify path and after every /auth/me
 * refresh.
 */
export function syncFeatureFlagFirm(
  user: Pick<
    UserData,
    "organization" | "organizationId" | "permissions" | "referringPortalEnabled" | "rootIdentity"
  >
): void {
  const firm = flagFirmFor(user);
  const userKind = flagUserKindFor(user);
  // Re-resolve when the firm OR the kind changes. (The slim login payload lacks
  // `referringPortalEnabled`, so the first /auth/me usually trips this too — redundant now that
  // the server reads the authoritative context, and one cheap request.)
  const key = `${firm?.firmId ?? ""}|${userKind}`;
  if (key === currentFlagKey) return;
  const hadKey = currentFlagKey !== null;
  currentFlagKey = key;
  // The first context of a session is fetched by identifyUser's own refresh; only a CHANGE needs one.
  if (hadKey) void refreshFeatureFlags();
}

export function resetAnalytics(): void {
  getSegment()?.reset();
  resetIdentity();
  resetFeatureFlags();
  currentFlagKey = null;
}

export function trackEvent(event: string, properties?: Record<string, unknown>): void {
  if (!shouldTrack()) return;
  const props = { ...properties, clientTimestamp: new Date().toISOString() };
  // Only send via Segment - it routes to Mixpanel via destination
  // Mixpanel SDK is only used for session recording
  getSegment()?.track(event, props);
}

export function trackPage(pageName: string, properties?: Record<string, unknown>): void {
  if (!shouldTrack()) return;
  const props = { ...properties, clientTimestamp: new Date().toISOString(), pageName };
  // Segment's page() call is automatically transformed to "Viewed {pageName}" by the Mixpanel destination
  // No need to also send directly via Mixpanel SDK (which would cause duplicate events)
  getSegment()?.page(pageName, props);
}
